var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useAccountActivityQuery, useAutopayStatusQuery } from "syf-api";
import useWindowSize from "syf-component-library/ui/customHooks/useWindowWidth";
import { formatCurrency, formatDisplayDate } from "syf-js-utilities";
import { useQueryClient } from "@tanstack/react-query";
import overlay from "../../helpers/isOverlay";
import CardLayout from "../../ui/layouts/CardLayout/CardLayout";
import findAccount from "../../helpers/findAccount";
import useFormatUndefined from "../../hooks/useFormatUndefined";
import { ErrorWrapper } from "../ErrorWrapper";
import { LoadingWrapper } from "../LoadingWrapper";
import { useConfig } from "../../hooks/useConfig";
import { getAccountType, getSetpayLoanProcessingStatus, getSynchronyAssetsURL, isInstallmentLoanMonthlyType, isPN4Type, isSecuredInstallment as isSecuredInstallmentLoan, } from "../../helpers";
import PRODUCT_TYPE, { CLOSED, DORMANT, PSCC, SETPAY, UPGRADING, } from "../../const/products";
import { useCardActivationQuery } from "../../queries/useCardActivation/useCardActivationQuery";
import { autopayModalContainerId } from "../../const/modalContainerId";
import { AccountType } from "../../helpers/getAccountType";
import isPayLaterLoanType from "../../helpers/isPayLaterLoanType";
import isAccountLinkDisabledForAClient from "../../helpers/isAccountLinkDisabledForAClient";
import shouldShowCreditBalanceRefund from "../../helpers/shouldShowCreditBalanceRefund/shouldShowCreditBalanceRefund";
import { useAccountFeatures, useAccountSummary, useCreditAccounts, } from "../../const/syfApiQueries";
var AccountCardMFE = function (_a) {
    var _b;
    var mfeConfig = _a.mfeConfig, accountId = _a.accountId, eventHandlers = _a.eventHandlers, _c = _a.isExpandedByDefault, isExpandedByDefault = _c === void 0 ? false : _c, handleAccountLink = _a.handleAccountLink, featureFlags = _a.featureFlags, shouldCallCreditAccountsQuery = _a.shouldCallCreditAccountsQuery;
    var _d = useState(), headerData = _d[0], setHeaderData = _d[1];
    var _e = useState(), bodyData = _e[0], setBodyData = _e[1];
    var _f = useState(), notificationsObject = _f[0], setNotificationsObject = _f[1];
    var _g = useState(), footerData = _g[0], setFooterData = _g[1];
    var _h = useState(false), isCardActivationRequired = _h[0], setCardActivationRequired = _h[1];
    var _j = useState(false), isViewAccountDisabled = _j[0], setIsViewAccountDisabled = _j[1];
    var hosts = useConfig().hosts;
    var queryClient = useQueryClient();
    var windowWidth = useWindowSize();
    var handleMAPClick = eventHandlers.handleMAPClick, handleRegisterAccountClick = eventHandlers.handleRegisterAccountClick;
    var _k = useAccountSummary(accountId), isAccountSummaryLoading = _k.isLoading, isAccountSummaryError = _k.isError, accountSummaryError = _k.error, accountSummaryData = _k.data;
    var isNotificationsEnabled = featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.isNotificationsEnabled;
    var formatUndefined = useFormatUndefined();
    var cardActivationData = useCardActivationQuery({
        config: mfeConfig,
        keyProps: {
            accountId: accountId,
            accountIdType: "guid",
        },
        queryOptions: __assign(__assign({}, queryClient.getQueryDefaults()), { enabled: !!accountId && !!isCardActivationRequired }),
    }).data;
    var autopayData = useAutopayStatusQuery({
        config: mfeConfig,
        keyProps: {
            accountId: accountId,
            accountIdType: "guid",
        },
        queryOptions: __assign(__assign({}, queryClient.getQueryDefaults()), { useErrorBoundary: false, enabled: Boolean((_b = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.conditions) === null || _b === void 0 ? void 0 : _b.includes("OPEN")) &&
                !!accountId }),
    }).data;
    var accountsData = useCreditAccounts(shouldCallCreditAccountsQuery).data;
    var activitiesData = useAccountActivityQuery({
        config: mfeConfig,
        keyProps: {
            accountId: accountId,
            accountIdType: "guid",
        },
        queryOptions: __assign(__assign({}, queryClient.getQueryDefaults()), { enabled: isNotificationsEnabled && !!accountId }),
    }).data;
    var featuresData = useAccountFeatures(accountId).data;
    var selectedAccount = findAccount((accountsData === null || accountsData === void 0 ? void 0 : accountsData.accounts) || [], accountId);
    var handleLinkClick = function (clientId, account) {
        if (!isViewAccountDisabled && handleAccountLink && account) {
            setIsViewAccountDisabled(true);
            handleAccountLink(account, clientId, windowWidth);
        }
    };
    useEffect(function () {
        if (accountSummaryData) {
            var accountInfo = accountSummaryData.accountInfo;
            var multiplePresentationInstruments = accountInfo.multiplePresentationInstruments, cardActivationRequired = accountInfo.cardActivationRequired;
            if (multiplePresentationInstruments || cardActivationRequired) {
                setCardActivationRequired(true);
            }
        }
    }, [accountSummaryData]);
    useEffect(function () {
        if (isNotificationsEnabled && activitiesData && accountSummaryData) {
            var notificationsData = {
                activity: activitiesData.activity,
                billing: accountSummaryData.billing,
                conditions: accountSummaryData.conditions,
                credit: accountSummaryData.credit,
                enrollmentDetails: autopayData === null || autopayData === void 0 ? void 0 : autopayData.enrollmentDetails,
                installment: accountSummaryData.installment,
                isRegistered: selectedAccount
                    ? selectedAccount.registration.registered
                    : false,
                product: accountSummaryData.product.type,
                enrollments: accountSummaryData.enrollments,
                featureFlags: {
                    isIRLEnabled: true,
                    isPreDeliveryMessageEnabled: true,
                },
            };
            setNotificationsObject(notificationsData);
        }
    }, [
        autopayData,
        activitiesData,
        accountSummaryData,
        selectedAccount,
        isNotificationsEnabled,
    ]);
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(function () {
        var _a, _b, _c, _d;
        if (isAccountSummaryLoading)
            return;
        if (accountSummaryData) {
            var installment = accountSummaryData.installment, billing = accountSummaryData.billing, accountInfo = accountSummaryData.accountInfo, product = accountSummaryData.product, credit = accountSummaryData.credit, conditions = accountSummaryData.conditions, enrollments = accountSummaryData.enrollments;
            var _e = installment || {}, principalAmount = _e.principalAmount, nextPaymentDueAmount = _e.nextPaymentDueAmount, endDate = _e.endDate;
            var nextPaymentDate = billing.nextPaymentDate, totalMinimumPaymentDue = billing.totalMinimumPaymentDue, waivedInterest = billing.waivedInterest;
            var currentBalance = credit.currentBalance, availableCredit = credit.availableCredit;
            var accountLast4 = accountInfo.accountLast4, nickName = accountInfo.nickName;
            var displayName = product.displayName, portfolioLogo = product.portfolioLogo, type = product.type, lineOfBusiness_1 = product.lineOfBusiness, clientId_1 = product.clientId;
            var cardArtURL = (_a = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.card) === null || _a === void 0 ? void 0 : _a.cardArtURL;
            var accountType = getAccountType(type);
            var isRegistered = (_b = selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.registration) === null || _b === void 0 ? void 0 : _b.registered;
            var isOverlay = overlay({
                isRegistered: isRegistered,
                accountType: accountType,
                conditions: conditions,
            });
            var isClosed = conditions === null || conditions === void 0 ? void 0 : conditions.includes(CLOSED);
            var isPSCCRevolvingAccount = lineOfBusiness_1 === PSCC;
            var isLoanPaidOff = isPayLaterLoanType(type) && isClosed;
            var isSetpay = product.type === PRODUCT_TYPE.INSTALLMENT_LOAN;
            var isPN4 = isPN4Type(type);
            var isInstallmentLoanMonthly = isInstallmentLoanMonthlyType(type);
            var isSecuredInstallment = isSecuredInstallmentLoan(type);
            var isSetpayLoanProcessing = getSetpayLoanProcessingStatus(installment, type, conditions);
            var isAccountUpgrading = (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.isAccountUpgradingMessageEnabled) &&
                (conditions === null || conditions === void 0 ? void 0 : conditions.includes(UPGRADING));
            /**
             * Revolving accounts -  J.Crew, Virgin, and all of PSCC are disabled
             * All other RC clients should be enabled (ex. Amazon, Sams, TJX)
             * Setpay legacy - disabled (Installment Loan)
             * Pay Later - disabled (PN4 & Pay Monthly)
             * Secured Installments - disabled
             */
            var isLinkEnabled = !!isRegistered &&
                !isViewAccountDisabled &&
                !isSetpayLoanProcessing &&
                !isAccountUpgrading &&
                !!clientId_1 &&
                !isSetpay &&
                !isPN4 &&
                !isInstallmentLoanMonthly &&
                lineOfBusiness_1 !== SETPAY &&
                !isPSCCRevolvingAccount &&
                !isSecuredInstallment &&
                !!handleAccountLink &&
                !isAccountLinkDisabledForAClient(clientId_1);
            var loanAmount = formatUndefined(principalAmount, formatCurrency);
            var paymentAmount = formatUndefined(nextPaymentDueAmount, formatCurrency);
            var paymentDateFullDate = formatUndefined(nextPaymentDate, formatDisplayDate);
            var creditBalance = formatUndefined(currentBalance, formatCurrency);
            var availableToSpend = formatUndefined(availableCredit, formatCurrency);
            var minimumPaymentDue = formatUndefined(totalMinimumPaymentDue, formatCurrency);
            var promoEndDate = waivedInterest === null || waivedInterest === void 0 ? void 0 : waivedInterest.promoEndDate;
            var isLoan = [AccountType.LOAN, AccountType.PAY_IN_4].includes(accountType);
            var isLoanPaidOffForSI = isSecuredInstallment && (conditions === null || conditions === void 0 ? void 0 : conditions.includes(DORMANT));
            var altText = isLoan ? "Pay Later with Synchrony" : "".concat(displayName);
            var paymentDate = paymentDateFullDate;
            setHeaderData({
                accountLast4: accountLast4,
                displayName: displayName,
                nickName: nickName,
                // TODO: Will need to uncomment condition when loan nickname update endpoint is ready
                isNickNameButton: false,
                accountId: accountId,
                portfolioLogo: portfolioLogo ||
                    "".concat(getSynchronyAssetsURL(hosts.assets), "/assets/synchrony/logo.svg"),
                isExpandedByDefault: isExpandedByDefault,
                isLoan: isLoan,
                isLinkEnabled: isLinkEnabled,
                handleAccountLink: function () {
                    return handleLinkClick(clientId_1 || "", selectedAccount);
                },
                accountType: accountType,
                featureFlags: featureFlags,
            });
            var formattedEndDate = formatUndefined(endDate, formatDisplayDate);
            var bodyDetailData = {
                loanAmount: loanAmount,
                paymentAmount: paymentAmount,
                paymentDate: paymentDate,
                isPN4: isPN4,
                cardArtURL: cardArtURL,
                isInstallmentLoanMonthly: isInstallmentLoanMonthly,
                creditBalance: creditBalance,
                minimumPaymentDue: minimumPaymentDue,
                availableToSpend: availableToSpend,
                promoEndDate: promoEndDate,
                /*
                 * This is so we have a number instead of a currency string for the autopay btn check.
                 * Will refactor later.
                 */
                unformattedCurrentBalance: currentBalance,
                accountType: accountType,
                isPastDue: billing === null || billing === void 0 ? void 0 : billing.pastDue,
                isSetpay: (product === null || product === void 0 ? void 0 : product.type) === "INSTALLMENT_LOAN",
                endDate: formattedEndDate,
            };
            setBodyData({
                isAutopayStatusUndefined: (autopayData === null || autopayData === void 0 ? void 0 : autopayData.enrollment) === undefined,
                isAutopayButtonVisible: !isPN4,
                isAutoPayOn: (_c = autopayData === null || autopayData === void 0 ? void 0 : autopayData.enrollment) !== null && _c !== void 0 ? _c : false,
                isViewAccountDisabled: isViewAccountDisabled,
                data: bodyDetailData,
                autopayModalContainerId: autopayModalContainerId(accountId),
                isOverlay: isOverlay,
                conditions: conditions,
                isIntroRateLoan: (featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.isIRLEnabled) &&
                    (enrollments === null || enrollments === void 0 ? void 0 : enrollments.includes("INTRO_RATE_LOAN")),
                handleAutopayClick: handleMAPClick,
                altText: altText,
                isLoanPaidOffForSI: isLoanPaidOffForSI,
                isLoanPaidOff: isLoanPaidOff,
                featureFlags: featureFlags,
                customerAssistancePlan: billing.customerAssistancePlan,
                settlementEnrolled: billing.settlementEnrolled,
                isRegistered: isRegistered,
            });
            var isManageBalance = accountType === AccountType.CARD &&
                shouldShowCreditBalanceRefund({
                    accountSummaryData: accountSummaryData,
                    accessToCreditBalanceRefund: featuresData === null || featuresData === void 0 ? void 0 : featuresData.accessToCreditBalanceRefund,
                    isCBRFeatureFlagEnabled: featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.isCBREnabled,
                    isRegistered: isRegistered,
                });
            var canRegisterAccount = handleRegisterAccountClick && lineOfBusiness_1 && clientId_1;
            setFooterData(__assign(__assign({}, eventHandlers), { 
                /*
                 * We need to pass the lineOfBusiness and clientId to the event handler
                 * we get from Vista so that we aren't making duplicate calls in the channel app
                 */
                handleRegisterAccountClick: canRegisterAccount
                    ? function () { return handleRegisterAccountClick(lineOfBusiness_1, clientId_1); }
                    : undefined, isManageBalance: isManageBalance, hasAccountStatements: featuresData === null || featuresData === void 0 ? void 0 : featuresData.accessToStatements, hasAccountRewards: (_d = featuresData === null || featuresData === void 0 ? void 0 : featuresData.rewardDetails) === null || _d === void 0 ? void 0 : _d.accessToRewards, isCardActivationEligible: (cardActivationData === null || cardActivationData === void 0 ? void 0 : cardActivationData.eligible) || false, isOverlay: isOverlay, accountId: accountId, isLoanPaidOff: isLoanPaidOff, featureFlags: featureFlags, shouldCallCreditAccountsQuery: shouldCallCreditAccountsQuery }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAccountSummaryLoading,
        isAccountSummaryError,
        accountSummaryError,
        accountSummaryData,
        featuresData,
        autopayData,
        cardActivationData,
    ]);
    return (_jsx(LoadingWrapper, __assign({ isLoading: isAccountSummaryLoading }, { children: _jsx(ErrorWrapper, __assign({ isError: isAccountSummaryError, isExpandedByDefault: isExpandedByDefault }, { children: headerData && bodyData && footerData ? (_jsx(CardLayout, { notificationsObject: notificationsObject, cardData: {
                    headerData: headerData,
                    bodyData: bodyData,
                    footerData: footerData,
                } })) : null })) })));
};
export default AccountCardMFE;
//# sourceMappingURL=AccountCardMFE.js.map